/**
 * Usage Dashboard Page
 * 
 * Displays consolidated AI usage statistics including:
 * - LLM summary cards (tokens, models, requests, latency)
 * - Embedding summary cards (tokens, vectors, requests, latency)
 * - Usage breakdown by model (pie chart)
 * - Usage breakdown by agent/tenant (bar chart)
 * - Model usage tables with details
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber, formatCompactNumber } from '@/lib/utils';
import { AccessDeniedModal } from 'shared/components/errors';
import { useUsageDashboard, useAnalyticsAccess, hasAnalyticsAccess } from '../hooks';
import { UsageChart, DateRangePicker, TenantSelector } from '../components';
import type { DateRange } from '../types';

// Icons from lucide-react
import { Zap, MessageSquare, Clock, Activity, Database, Box } from 'lucide-react';

export function UsageDashboardPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [tenantFilter, setTenantFilter] = useState<string | null>(null);

  // Access gate (REQ-ANL-002): fail-closed, no data rendered without permission
  const { data: accessData, isLoading: accessLoading } = useAnalyticsAccess();
  const { data: dashboardData, isLoading, error } = useUsageDashboard(dateRange, tenantFilter);

  const stats = dashboardData?.stats;
  const byModel = dashboardData?.by_model || [];
  const byAgent = dashboardData?.by_agent || [];
  const byTenant = dashboardData?.by_tenant || [];
  const daily = dashboardData?.daily || [];
  const embeddingDaily = dashboardData?.embedding_daily || [];
  const isSystemView = dashboardData?.isSystemView ?? false;

  // Embedding data
  const embeddingStats = dashboardData?.embedding_stats;
  const embeddingByModel = dashboardData?.embedding_by_model || [];

  // Calculate total tokens for display
  const totalTokens = (stats?.total_input_tokens || 0) + (stats?.total_output_tokens || 0);
  const hasEmbeddingData = embeddingStats && embeddingStats.request_count > 0;

  // While the access check is in flight show nothing (avoid flash of content)
  if (accessLoading) {
    return null;
  }

  // Direct URL without permission → representative denial (SEC-003 fail-closed)
  if (!hasAnalyticsAccess(accessData)) {
    const handleGoHome = () => navigate('/');
    return (
      <AccessDeniedModal
        isOpen
        onClose={handleGoHome}
        onGoBack={handleGoHome}
        supportEmail="support@atlas.local"
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with DateRangePicker */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Usage Dashboard</h1>
          <p className="text-muted-foreground">Monitor AI usage across LLM and embedding models</p>
        </div>
        <div className="flex items-center gap-4">
          {isSystemView && (
            <TenantSelector value={tenantFilter} onChange={setTenantFilter} />
          )}
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">
            Failed to load usage data: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {/* LLM Summary Cards */}
      {!isLoading && !error && (
        <div>
          <h2 className="text-lg font-semibold mb-3">LLM Usage</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCompactNumber(totalTokens)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatNumber(stats?.total_input_tokens)} in / {formatNumber(stats?.total_output_tokens)} out
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Models Used</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats?.models_used?.length || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Distinct models
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Requests</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(stats?.request_count)}
                </div>
                <p className="text-xs text-muted-foreground">
                  LLM invocations
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats?.avg_latency_ms?.toFixed(0) || '0'}ms
                </div>
                <p className="text-xs text-muted-foreground">
                  Response time
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Loading skeleton for cards */}
      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      )}

      {/* Embedding Summary Cards */}
      {!isLoading && !error && hasEmbeddingData && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Embedding Usage</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Embedding Tokens</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCompactNumber(embeddingStats?.total_input_tokens || 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Input tokens processed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Vectors Generated</CardTitle>
                <Box className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCompactNumber(embeddingStats?.total_vectors || 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Total vectors
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Embedding Requests</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(embeddingStats?.request_count)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Embedding operations
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {embeddingStats?.avg_latency_ms?.toFixed(0) || '0'}ms
                </div>
                <p className="text-xs text-muted-foreground">
                  Embedding response time
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Charts */}
      {!isLoading && !error && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>LLM Usage by Model</CardTitle>
            </CardHeader>
            <CardContent>
              <UsageChart
                data={byModel}
                dataKey="total_tokens"
                nameKey="model_id"
                type="pie"
                formatValue={formatCompactNumber}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{isSystemView ? 'LLM Usage by Tenant' : 'LLM Usage by Agent'}</CardTitle>
            </CardHeader>
            <CardContent>
              {isSystemView ? (
                <UsageChart
                  data={byTenant}
                  dataKey="total_tokens"
                  nameKey="tenant_name"
                  type="bar"
                  formatValue={formatCompactNumber}
                />
              ) : (
                <UsageChart
                  data={byAgent}
                  dataKey="total_tokens"
                  nameKey="agent_name"
                  type="bar"
                  formatValue={formatCompactNumber}
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Usage Time Series */}
      {!isLoading && !error && (daily.length > 0 || embeddingDaily.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {daily.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>LLM Daily Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <UsageChart
                  data={daily}
                  dataKey="total_tokens"
                  nameKey="date"
                  type="line"
                  secondaryDataKey="request_count"
                  formatValue={formatCompactNumber}
                />
              </CardContent>
            </Card>
          )}
          {embeddingDaily.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Embedding Daily Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <UsageChart
                  data={embeddingDaily}
                  dataKey="input_tokens"
                  nameKey="date"
                  type="line"
                  secondaryDataKey="vector_count"
                  formatValue={formatCompactNumber}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Embedding Model Chart */}
      {!isLoading && !error && embeddingByModel.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Embedding Usage by Model</CardTitle>
            </CardHeader>
            <CardContent>
              <UsageChart
                data={embeddingByModel.map(m => ({ ...m, total_tokens: m.input_tokens }))}
                dataKey="total_tokens"
                nameKey="model_id"
                type="pie"
                formatValue={formatCompactNumber}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* LLM Model Breakdown Table */}
      {!isLoading && !error && byModel.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>LLM Model Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b">
                    <th className="pb-2 font-medium">Model</th>
                    <th className="pb-2 text-right font-medium">Input Tokens</th>
                    <th className="pb-2 text-right font-medium">Output Tokens</th>
                    <th className="pb-2 text-right font-medium">Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {byModel.map((model) => (
                    <tr key={model.model_id} className="border-b last:border-0">
                      <td className="py-3 font-medium">{model.model_id}</td>
                      <td className="py-3 text-right">{formatNumber(model.input_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(model.output_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(model.request_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Embedding Model Breakdown Table */}
      {!isLoading && !error && embeddingByModel.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Embedding Model Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b">
                    <th className="pb-2 font-medium">Model</th>
                    <th className="pb-2 text-right font-medium">Input Tokens</th>
                    <th className="pb-2 text-right font-medium">Vectors</th>
                    <th className="pb-2 text-right font-medium">Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {embeddingByModel.map((model) => (
                    <tr key={model.model_id} className="border-b last:border-0">
                      <td className="py-3 font-medium">{model.model_id}</td>
                      <td className="py-3 text-right">{formatNumber(model.input_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(model.vector_count)}</td>
                      <td className="py-3 text-right">{formatNumber(model.request_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tenant Breakdown Table (System View) */}
      {!isLoading && !error && isSystemView && byTenant.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tenant Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b">
                    <th className="pb-2 font-medium">Tenant</th>
                    <th className="pb-2 text-right font-medium">Input Tokens</th>
                    <th className="pb-2 text-right font-medium">Output Tokens</th>
                    <th className="pb-2 text-right font-medium">Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {byTenant.map((tenant) => (
                    <tr key={tenant.tenant_id} className="border-b last:border-0">
                      <td className="py-3 font-medium">{tenant.tenant_name}</td>
                      <td className="py-3 text-right">{formatNumber(tenant.input_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(tenant.output_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(tenant.request_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Breakdown Table (Tenant View) */}
      {!isLoading && !error && !isSystemView && byAgent.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Agent Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b">
                    <th className="pb-2 font-medium">Agent</th>
                    <th className="pb-2 text-right font-medium">Input Tokens</th>
                    <th className="pb-2 text-right font-medium">Output Tokens</th>
                    <th className="pb-2 text-right font-medium">Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {byAgent.map((agent) => (
                    <tr key={agent.agent_id} className="border-b last:border-0">
                      <td className="py-3 font-medium">{agent.agent_name}</td>
                      <td className="py-3 text-right">{formatNumber(agent.input_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(agent.output_tokens)}</td>
                      <td className="py-3 text-right">{formatNumber(agent.request_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !error && byModel.length === 0 && embeddingByModel.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <p>No usage data available for the selected period.</p>
            <p className="text-sm mt-2">Usage data will appear here once LLM or embedding requests are made.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
